import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dialog", "title", "folderList", "createForm", "renameForm",
    "folderName", "renameName", "confirmBtn", "createBtn", "renameBtn"
  ]

  static values = {
    projectId: String,
    currentFolderId: String
  }

  #mode = null // "move", "create", or "rename"
  #assetIds = []
  #selectedFolderId = null
  #renameFolderId = null

  // Open modal for moving assets
  openMove(event) {
    this.#mode = "move"
    this.#assetIds = event.detail?.assetIds || this.#getSelectedAssetIds()

    this.titleTarget.textContent = `Move ${this.#assetIds.length} ${this.#assetIds.length === 1 ? 'file' : 'files'}`
    this.#showFolderList()
    this.#showButton("confirm", "Move")
    this.dialogTarget.showModal()
  }

  // Open modal for creating a new folder
  openCreate(event) {
    this.#mode = "create"
    const parentFolderId = event?.detail?.parentFolderId || null

    this.titleTarget.textContent = "New Folder"
    this.#showCreateForm()
    this.#showButton("create", "Create Folder")
    this.dialogTarget.showModal()

    // Focus the input
    setTimeout(() => this.folderNameTarget.focus(), 100)
  }

  // Open modal for renaming a folder
  openRename(event) {
    this.#mode = "rename"
    // Support both event.detail (from dispatch) and params (from data-attributes)
    this.#renameFolderId = event.detail?.folderId || event.params?.folderId
    const currentName = event.detail?.currentName || event.params?.currentName || ""

    this.titleTarget.textContent = "Rename Folder"
    this.renameNameTarget.value = currentName
    this.#showRenameForm()
    this.#showButton("rename", "Rename")
    this.dialogTarget.showModal()

    // Focus and select the input
    setTimeout(() => {
      this.renameNameTarget.focus()
      this.renameNameTarget.select()
    }, 100)
  }

  selectFolder(event) {
    this.#selectedFolderId = event.currentTarget.dataset.folderId

    // Visual feedback - highlight selected
    this.folderListTarget.querySelectorAll("button").forEach(btn => {
      btn.classList.remove("bg-blue-100", "border-blue-500")
    })
    event.currentTarget.classList.add("bg-blue-100", "border-blue-500")
  }

  async confirm() {
    if (this.#mode === "move") {
      await this.#moveAssets()
    }
  }

  async createFolder() {
    const name = this.folderNameTarget.value.trim()
    if (!name) return

    const folderData = { name }
    // Include parent folder if we're currently viewing a folder
    if (this.currentFolderIdValue) {
      folderData.parent_folder_id = this.currentFolderIdValue
    }

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/folders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({ folder: folderData })
      })

      if (response.ok) {
        this.close()
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        console.error("Create folder failed:", await response.text())
      }
    } catch (error) {
      console.error("Create folder error:", error)
    }
  }

  async renameFolder() {
    const name = this.renameNameTarget.value.trim()
    if (!name || !this.#renameFolderId) return

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/folders/${this.#renameFolderId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({ folder: { name: name } })
      })

      if (response.ok) {
        this.close()
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        console.error("Rename folder failed:", await response.text())
      }
    } catch (error) {
      console.error("Rename folder error:", error)
    }
  }

  close() {
    this.dialogTarget.close()
    this.#reset()
  }

  // Private methods

  async #moveAssets() {
    if (this.#assetIds.length === 0) return

    try {
      const response = await fetch(`/projects/${this.projectIdValue}/assets/bulk_move`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({
          asset_ids: this.#assetIds,
          folder_id: this.#selectedFolderId || null
        })
      })

      if (response.ok) {
        this.close()
        // Clear selection and refresh
        this.#clearSelection()
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        console.error("Move assets failed:", await response.text())
      }
    } catch (error) {
      console.error("Move assets error:", error)
    }
  }

  #showFolderList() {
    this.folderListTarget.classList.remove("hidden")
    this.createFormTarget.classList.add("hidden")
    this.renameFormTarget.classList.add("hidden")
  }

  #showCreateForm() {
    this.folderListTarget.classList.add("hidden")
    this.createFormTarget.classList.remove("hidden")
    this.renameFormTarget.classList.add("hidden")
    this.folderNameTarget.value = ""
  }

  #showRenameForm() {
    this.folderListTarget.classList.add("hidden")
    this.createFormTarget.classList.add("hidden")
    this.renameFormTarget.classList.remove("hidden")
  }

  #showButton(type, label) {
    this.confirmBtnTarget.classList.add("hidden")
    this.createBtnTarget.classList.add("hidden")
    this.renameBtnTarget.classList.add("hidden")

    if (type === "confirm") {
      this.confirmBtnTarget.textContent = label
      this.confirmBtnTarget.classList.remove("hidden")
    } else if (type === "create") {
      this.createBtnTarget.classList.remove("hidden")
    } else if (type === "rename") {
      this.renameBtnTarget.classList.remove("hidden")
    }
  }

  #reset() {
    this.#mode = null
    this.#assetIds = []
    this.#selectedFolderId = null
    this.#renameFolderId = null
  }

  #getSelectedAssetIds() {
    const checkboxes = document.querySelectorAll('[data-bulk-select-target="checkbox"]:checked')
    return Array.from(checkboxes).map(cb => cb.dataset.assetId)
  }

  #clearSelection() {
    const container = document.querySelector('[data-controller~="bulk-select"]')
    if (container) {
      const controller = this.application.getControllerForElementAndIdentifier(container, "bulk-select")
      if (controller) controller.clearSelection()
    }
  }

  #getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
